<template>
  <div class="border-b bg-white">
    <div class="d-flex align-center px-4 py-3 cursor-pointer" @click="expanded = !expanded">
      <span class="text-caption font-weight-bold text-uppercase" style="color: #282c3f">{{
        title
      }}</span>
      <v-spacer></v-spacer>
      <span
        v-if="hasSelection"
        class="text-xs font-medium primary--text cursor-pointer mr-2"
        style="text-transform: none"
        @click.stop="clearSelection"
      >
        Clear Selection
      </span>
      <v-icon size="18">{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </div>

    <v-expand-transition>
      <div v-show="expanded" class="px-4 pb-3">
        <!-- Debounced Search Input -->
        <div v-if="searchable && items.length >= 6" class="mb-2 ecom-search">
          <v-text-field
            v-model="searchQuery"
            :placeholder="`Search for ${title}`"
            dense
            solo
            flat
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            background-color="#f5f5f6"
            @input="debouncedSearch"
            height="36"
            class="text-caption"
          ></v-text-field>
        </div>

        <!-- Options Container -->
        <div class="options-container" style="max-height: 200px; overflow-y: auto">
          <slot v-if="$slots.default"></slot>

          <template v-else>
            <div
              v-if="filteredItems.length === 0"
              class="text-caption text-center py-2"
              style="color: #7e818c"
            >
              No matches found
            </div>
            <template v-else>
              <template v-if="multiple">
                <v-checkbox
                  v-for="(item, i) in displayedItems"
                  :key="i"
                  v-model="internalValue"
                  :value="getValue(item)"
                  :label="getLabel(item)"
                  hide-details
                  dense
                  color="primary"
                  :disabled="disabled || itemDisabled(item)"
                  class="mt-0 pt-0 mb-1 ecom-checkbox text-caption"
                  @change="handleChange"
                >
                  <template v-slot:label>
                    <span
                      class="text-body-2"
                      :style="{ color: disabled || itemDisabled(item) ? '#9e9e9e' : '#282c3f' }"
                      >{{ getLabel(item) }}</span
                    >
                  </template>
                </v-checkbox>
              </template>
              <template v-else>
                <v-radio-group
                  v-model="internalValue"
                  hide-details
                  :disabled="disabled"
                  class="mt-0 pt-2 pl-2"
                  @change="handleChange"
                >
                  <v-radio
                    v-for="(item, i) in displayedItems"
                    :key="i"
                    :value="getValue(item)"
                    :label="getLabel(item)"
                    color="primary"
                    :disabled="itemDisabled(item)"
                    class="mb-2 ecom-radio text-caption"
                  >
                    <template v-slot:label>
                      <span
                        class="text-body-2"
                        :style="{ color: disabled || itemDisabled(item) ? '#9e9e9e' : '#282c3f' }"
                        >{{ getLabel(item) }}</span
                      >
                    </template>
                  </v-radio>
                </v-radio-group>
              </template>
            </template>
          </template>
        </div>

        <!-- Show More/Less Toggle -->
        <div
          v-if="!$slots.default && filteredItems.length > showLimit"
          class="mt-1 text-xs font-semibold cursor-pointer pl-8 primary--text"
          @click="showAll = !showAll"
        >
          <span v-if="!showAll">+ {{ filteredItems.length - showLimit }} more</span>
          <span v-else>- Show Less</span>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
export default {
  name: 'EcomFilterCategory',
  props: {
    title: { type: String, required: true },
    items: { type: Array, default: () => [] },
    value: {},
    itemText: { type: String, default: 'label' },
    itemValue: { type: String, default: 'value' },
    searchable: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    showLimit: { type: Number, default: 6 },
    disabled: { type: Boolean, default: false },
    itemDisabled: { type: Function, default: () => false }
  },
  data() {
    return {
      expanded: true,
      searchQuery: '',
      dbQuery: '',
      showAll: false,
      showCount: this.showLimit,
      debounceTimer: null
    }
  },
  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    filteredItems() {
      if (!this.items || this.items.length === 0) return []
      if (!this.dbQuery) return this.items

      const query = this.dbQuery.toLowerCase()
      return this.items.filter((item) => {
        const text = this.getLabel(item)
        return text && String(text).toLowerCase().includes(query)
      })
    },
    displayedItems() {
      if (this.showAll) return this.filteredItems
      return this.filteredItems.slice(0, this.showCount)
    },
    hasSelection() {
      if (this.multiple) {
        return Array.isArray(this.value) && this.value.length > 0
      }
      return this.value !== null && this.value !== undefined && this.value !== ''
    }
  },
  methods: {
    getLabel(item) {
      if (typeof item === 'object') return item[this.itemText]
      return String(item)
    },
    getValue(item) {
      if (typeof item === 'object') return item[this.itemValue]
      return String(item)
    },
    clearSelection() {
      if (this.multiple) {
        this.internalValue = []
      } else {
        this.internalValue = null
      }
      this.handleChange(this.internalValue)
    },
    debouncedSearch(val) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.dbQuery = val || ''
      }, 300)
    },
    handleChange(val) {
      this.$emit('change', val)
    }
  },
  watch: {
    value(val) {
      if (
        val === null ||
        val === undefined ||
        val === '' ||
        (Array.isArray(val) && val.length === 0)
      ) {
        this.searchQuery = ''
        this.dbQuery = ''
      }
    }
  }
}
</script>

<style scoped>
.border-b {
  border-bottom: 1px solid #e9e9ed;
}
.ecom-search >>> .v-input__control {
  min-height: 36px !important;
}
.ecom-search >>> .v-input__slot {
  box-shadow: none !important;
  border: 1px solid #f5f5f6;
  border-radius: 4px;
}
.ecom-search >>> .v-input__slot:hover {
  border-color: #d4d5d9;
}
.ecom-checkbox >>> .v-input--selection-controls__input {
  margin-right: 4px;
}
.ecom-radio >>> .v-input--selection-controls__input {
  margin-right: 4px;
}
/* .options-container::-webkit-scrollbar {
  width: 4px;
  
}
.options-container::-webkit-scrollbar-thumb {
  background: #c3c4c7;
  border-radius: 4px;
} */
</style>
